import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';
import { appPath } from '../../../routes/paths';
import { ACTIVITIES_NAV_ITEM } from '../copy';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  Empty,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  TrendingUp,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  CheckSquare,
  Settings,
  List,
  Inbox,
  CircleStop,
  MinusCircle,
  AlertCircle,
  Zap,
  Download,
} from 'lucide-react';
import { useTaskDashboard } from '../hooks/useTaskDashboard';
import type {
  GetTaskDashboardParams,
  TaskDashboardStageRow,
} from '../api/taskDashboard';
import type { TaskItemListItem, TaskItemStatus } from '../types';
import { TASK_TYPE_INTERNAL } from '../constants';
import TaskActivityRowActions from './TaskActivityRowActions';
import TaskActivityPrimaryCell from './TaskActivityPrimaryCell';
import TaskDetailDrawer from './TaskDetailDrawer';
import TaskFormDrawer from './TaskFormDrawer';
import CompleteRecurringModal from './CompleteRecurringModal';
import ResolveFollowupModal from './ResolveFollowupModal';
import SnoozeFollowupModal from './SnoozeFollowupModal';
import { useUpdateTaskStatus } from '../hooks/useUpdateTaskStatus';
import { useDeleteTaskItem } from '../hooks/useDeleteTaskItem';
import { useCompleteRecurringTask } from '../hooks/useCompleteRecurringTask';
import { useResolveFollowupTask } from '../hooks/useResolveFollowupTask';
import { useSnoozeFollowupTask } from '../hooks/useSnoozeFollowupTask';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { usePermission } from '../../permissions/PermissionsContext';
import TaskDashboardFilterBar, {
  defaultTaskDashboardMonthRange,
  type TaskDashboardFilterState,
} from './TaskDashboardFilterBar';
import { useUrlListFilter } from '../../../lib/query/useUrlListFilter';
import { DASHBOARD_FILTER_FIELDS } from '../../../routes/filterMaps';
import { formatAmount } from '../../business-overview/components/BusinessOverviewMetricCard';
import BusinessOverviewSection from '../../business-overview/components/BusinessOverviewSection';
import PipelineStageBarChart from '../../business-overview/components/PipelineStageBarChart';
import type { PipelineStageActivityItem } from '../../business-overview/components/PipelineStageBarChart';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useExportTaskDashboard } from '../hooks/useExportTaskDashboard';
import { useExportTaskDashboardPdf } from '../hooks/useExportTaskDashboardPdf';
import {
  activityDueIsOverdue,
  activityDueIsToday,
  formatActivityTableDate,
} from '../taskActivityTableFormat';
import PageScaffold from '../../../components/PageScaffold';
import { withDashboardContext, type DashboardFilterContext } from '../../../routes/filterNavigation';
import {
  TASK_FILTER_FIELDS,
} from '../../../routes/filterMaps';
import './TaskDashboardPage.css';

dayjs.extend(utc);

const { Text } = Typography;

const LIST_CAP = 50;

const ACTIONS_COLUMN_WIDTH = 56;

const funnelStageColors = ['#5DCAA5', '#EF9F27', '#7F77DD', '#D85A30', '#378ADD', '#1D9E75'];

type ExecutionBucketLabel = 'Overdue' | 'Due today' | 'Next 7 days' | 'Other open';

type ExecutionQueueRow = TaskItemListItem & {
  bucket: ExecutionBucketLabel;
  bucketOrder: number;
};

interface StageBreakdownRow extends TaskDashboardStageRow {
  conversionPercentFromPrevious: number | null;
}

const cardBodyPadding = { padding: '16px 20px' };

function sortOperationalItems(items: TaskItemListItem[]): TaskItemListItem[] {
  return [...items].sort((a, b) => {
    if (a.dueDate === null && b.dueDate === null) {
      return 0;
    }
    if (a.dueDate === null) {
      return 1;
    }
    if (b.dueDate === null) {
      return -1;
    }
    return dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf();
  });
}

function buildExecutionQueueRows(data: {
  operational: {
    overdue: { items: TaskItemListItem[] };
    dueToday: { items: TaskItemListItem[] };
    upcomingWeek: { items: TaskItemListItem[] };
    otherOpen: { items: TaskItemListItem[] };
  };
}): ExecutionQueueRow[] {
  const rows: ExecutionQueueRow[] = [];
  const seenIds = new Set<string>();

  const pushUnique = (
    item: TaskItemListItem,
    bucket: ExecutionBucketLabel,
    bucketOrder: number
  ) => {
    if (seenIds.has(item.id)) {
      return;
    }
    seenIds.add(item.id);
    rows.push({ ...item, bucket, bucketOrder });
  };

  sortOperationalItems(data.operational.overdue.items).forEach((item) =>
    pushUnique(item, 'Overdue', 0)
  );
  sortOperationalItems(data.operational.dueToday.items).forEach((item) =>
    pushUnique(item, 'Due today', 1)
  );
  sortOperationalItems(data.operational.upcomingWeek.items).forEach((item) =>
    pushUnique(item, 'Next 7 days', 2)
  );
  data.operational.otherOpen.items.forEach((item) =>
    pushUnique(item, 'Other open', 3)
  );
  rows.sort((a, b) => {
    if (a.bucketOrder !== b.bucketOrder) {
      return a.bucketOrder - b.bucketOrder;
    }
    if (a.dueDate === null && b.dueDate === null) {
      return 0;
    }
    if (a.dueDate === null) {
      return 1;
    }
    if (b.dueDate === null) {
      return -1;
    }
    return dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf();
  });
  return rows;
}

function stagesSorted(rows: TaskDashboardStageRow[] | undefined | null): TaskDashboardStageRow[] {
  return rows ? [...rows].sort((a, b) => a.sortOrder - b.sortOrder) : [];
}

function taskStagesToChartItems(rows: TaskDashboardStageRow[]): PipelineStageActivityItem[] {
  const sorted = stagesSorted(rows);
  return sorted.map((stage, index) => ({
    stageId: stage.id,
    stageLabel: stage.label,
    dotColor: funnelStageColors[index % funnelStageColors.length],
    actionCount: stage.openDealCount,
    activeRepCount: 0,
    description: `${stage.openDealCount} deals · ${formatAmount(stage.targetedAmountSum)}`,
  }));
}

function buildStageBreakdown(rows: TaskDashboardStageRow[]): StageBreakdownRow[] {
  const sorted = stagesSorted(rows);
  return sorted.map((stage, index) => {
    let conversionPercentFromPrevious: number | null = null;
    if (index > 0) {
      const previousCount = sorted[index - 1].openDealCount;
      conversionPercentFromPrevious =
        previousCount > 0 ? (stage.openDealCount / previousCount) * 100 : null;
    }
    return { ...stage, conversionPercentFromPrevious };
  });
}

function DashboardBody({ children }: { children: React.ReactNode }) {
  return <Row gutter={[16, 16]}>{children}</Row>;
}

const DashboardShell = memo(function DashboardShell({
  filters,
  onFiltersChange,
  staleNote,
  actions,
  children,
}: {
  filters: TaskDashboardFilterState;
  onFiltersChange: (value: TaskDashboardFilterState) => void;
  staleNote?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const filterElement = useMemo(
    () => <TaskDashboardFilterBar value={filters} onChange={onFiltersChange} staleNote={staleNote} />,
    [filters, onFiltersChange, staleNote],
  );

  return (
    <PageScaffold
      title="Task Dashboard"
      subtitle="Operational queue, pipeline funnel, and task KPIs for the selected period."
      actions={actions}
      filters={filterElement}
    >
      <DashboardBody>{children}</DashboardBody>
    </PageScaffold>
  );
});

const TaskDashboardPage: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { isScoped, assignedSegmentIds, defaultSegmentIdsForApi } = useUserSegmentScope();
  const isAdmin = usePermission('administrator.access');
  const defaultFilters: TaskDashboardFilterState = useMemo(() => ({
    dateRange: defaultTaskDashboardMonthRange(),
    segmentIds: [],
    contactCategoryIds: [],
    assigneeIds: [],
  }), []);

  const transformOptions = useMemo(() => ({
    transform: {
      deserialize: (params: URLSearchParams) => {
        const from = params.get('fromUtc');
        const to = params.get('toUtc');
        if (from && to) {
          return {
            dateRange: [dayjs(from), dayjs(to)] as [Dayjs, Dayjs],
          } as Partial<TaskDashboardFilterState>;
        }
        return {};
      },
      serialize: (f: TaskDashboardFilterState) => {
        if (f.dateRange) {
          return {
            fromUtc: f.dateRange[0].utc().startOf('day').toISOString(),
            toUtc: f.dateRange[1].utc().startOf('day').toISOString(),
          };
        }
        return { fromUtc: undefined, toUtc: undefined };
      },
    },
  }), []);

  const [filters, setFilters, , silentSetFilters] = useUrlListFilter(
    defaultFilters,
    DASHBOARD_FILTER_FIELDS,
    transformOptions,
  );

  useEffect(() => {
    if (!isScoped) {
      return;
    }
    silentSetFilters((prev: TaskDashboardFilterState) => ({
      ...prev,
      segmentIds: defaultSegmentIdsForApi(),
      assigneeIds: [],
    }));
  }, [isScoped, assignedSegmentIds.join(','), defaultSegmentIdsForApi, silentSetFilters]);
  const [showAssigneeColumn, setShowAssigneeColumn] = useState(true);

  const queryParams = useMemo((): GetTaskDashboardParams => {
    const params: GetTaskDashboardParams = {};
    if (filters.dateRange !== null) {
      const [from, to] = filters.dateRange;
      params.fromUtc = from.utc().startOf('day').toISOString();
      params.toUtc = to.utc().startOf('day').toISOString();
    }
    const segmentIds = defaultSegmentIdsForApi(filters.segmentIds);
    if (segmentIds.length > 0) {
      params.segmentIds = segmentIds;
    }
    if (filters.contactCategoryIds.length > 0) {
      params.contactCategoryIds = filters.contactCategoryIds;
    }
    if (isAdmin && filters.assigneeIds.length > 0) {
      params.assigneeIds = filters.assigneeIds;
    }
    params.timeZoneOffsetMinutes = -new Date().getTimezoneOffset();
    return params;
  }, [filters, isScoped, defaultSegmentIdsForApi]);

  const navTo = (
    path: string,
    overrides: Record<string, unknown>,
    fields: readonly import('../../../routes/filterMaps').FilterFieldDef[],
    forwardDateRange = path !== '/tasks',
  ) => {
    const ctx: DashboardFilterContext = {};
    if (forwardDateRange && filters.dateRange) {
      ctx.fromUtc = filters.dateRange[0].utc().startOf('day').toISOString();
      ctx.toUtc = filters.dateRange[1].utc().startOf('day').toISOString();
    }
    if (filters.segmentIds.length > 0) ctx.segmentIds = filters.segmentIds;
    if (filters.contactCategoryIds.length > 0) ctx.contactCategoryIds = filters.contactCategoryIds;
    if (filters.assigneeIds.length > 0) ctx.assigneeIds = filters.assigneeIds;
    navigate(withDashboardContext(path, ctx, overrides, fields));
  };

  const { data, isLoading, isError, isFetching, error, refetch, isPlaceholderData } = useTaskDashboard(queryParams);

  const queryClient = useQueryClient();
  const invalidateTaskDashboard = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['task-dashboard'] });
  }, [queryClient]);

  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [completeRecurringTaskId, setCompleteRecurringTaskId] = useState<string | null>(null);
  const [resolveTaskId, setResolveTaskId] = useState<string | null>(null);
  const [snoozeTaskId, setSnoozeTaskId] = useState<string | null>(null);
  const [queuePagination, setQueuePagination] = useState({ current: 1, pageSize: 25 });

  const statusMutation = useUpdateTaskStatus(invalidateTaskDashboard);
  const deleteMutation = useDeleteTaskItem(invalidateTaskDashboard);
  const exportMutation = useExportTaskDashboard();
  const pdfExportMutation = useExportTaskDashboardPdf();
  const completeRecurringMutation = useCompleteRecurringTask(() => {
    setCompleteRecurringTaskId(null);
    invalidateTaskDashboard();
  });

  const resolveMutation = useResolveFollowupTask(() => {
    setResolveTaskId(null);
    invalidateTaskDashboard();
  });

  const snoozeMutation = useSnoozeFollowupTask(() => {
    setSnoozeTaskId(null);
    invalidateTaskDashboard();
  });

  const handleView = useCallback((id: string) => {
    setViewTaskId(id);
  }, []);

  const handleEdit = useCallback((id: string) => {
    setEditTaskId(id);
    setFormDrawerOpen(true);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setFormDrawerOpen(false);
    setEditTaskId(null);
    invalidateTaskDashboard();
  }, [invalidateTaskDashboard]);

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation],
  );

  const handleStatusChange = useCallback(
    (id: string, status: TaskItemStatus) => {
      statusMutation.mutate({ id, status });
    },
    [statusMutation],
  );

  const handleExportDashboard = useCallback((format: 'excel' | 'pdf') => {
    const params: GetTaskDashboardParams = {
      fromUtc: queryParams.fromUtc,
      toUtc: queryParams.toUtc,
      segmentIds: queryParams.segmentIds,
      contactCategoryIds: queryParams.contactCategoryIds,
      assigneeIds: queryParams.assigneeIds,
      timeZoneOffsetMinutes: queryParams.timeZoneOffsetMinutes,
    };
    if (format === 'excel') {
      exportMutation.mutate(params);
    } else {
      pdfExportMutation.mutate(params);
    }
  }, [queryParams, exportMutation, pdfExportMutation]);

  const dashboardExportMenuItems: MenuProps['items'] = [
    { key: 'excel', icon: <FileSpreadsheet size={14} />, label: 'Excel (.xlsx)' },
    { key: 'pdf', icon: <FileText size={14} />, label: 'PDF (.pdf)' },
  ];

  const { data: users = [] } = useUserOptions();
  const userLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      map.set(u.id, u.label);
    }
    return map;
  }, [users]);

  const queueBucketTag = (bucket: ExecutionBucketLabel) => (
    <Tag
      color={bucket === 'Overdue' ? 'red' : bucket === 'Due today' ? 'orange' : bucket === 'Other open' ? 'default' : 'blue'}
      style={{ marginInlineEnd: 0, fontSize: 11 }}
    >
      {bucket}
    </Tag>
  );

  const executionColumns: ColumnsType<ExecutionQueueRow> = useMemo(() => {
    const cols: ColumnsType<ExecutionQueueRow> = [
      {
        title: 'SN',
        key: 'sn',
        width: 56,
        align: 'center',
        render: (_, __, index) => (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {(queuePagination.current - 1) * queuePagination.pageSize + index + 1}
          </Typography.Text>
        ),
      },
      {
        title: 'Queue',
        key: 'bucket',
        width: 100,
        render: (_, record) => queueBucketTag(record.bucket),
      },
      {
        title: 'Activity',
        key: 'activity',
        width: 260,
        render: (_, record) => (
          <TaskActivityPrimaryCell
            record={record}
            onViewTitle={handleView}
          />
        ),
      },
      {
        title: 'Due',
        dataIndex: 'dueDate',
        key: 'dueDate',
        width: 96,
        render: (date: string | null, record) => {
          const overdue = activityDueIsOverdue(date, record.status);
          const dueToday = activityDueIsToday(date);
          const color = overdue ? 'error' : dueToday ? 'warning' : undefined;
          const label = formatActivityTableDate(date);
          return (
            <Tooltip title={overdue ? 'Overdue' : dueToday ? 'Due today' : undefined}>
              <span className={color ? `td-due-${color}` : undefined}>{label}</span>
            </Tooltip>
          );
        },
      },
      {
        title: 'Issue',
        dataIndex: 'createdAtUtc',
        key: 'createdAtUtc',
        width: 90,
        render: (d: string) => formatActivityTableDate(d),
      },
    ];
    if (showAssigneeColumn) {
      cols.push({
        title: 'Assignee',
        dataIndex: 'assigneeId',
        key: 'assigneeId',
        width: 110,
        responsive: ['md' as const],
        ellipsis: true,
        render: (assigneeId: string | null, record) =>
          assigneeId
            ? userLookup.get(assigneeId) ?? (record as ExecutionQueueRow).assigneeDisplayName ?? '—'
            : '—',
      });
    }
    cols.push({
      title: '#',
      key: 'actions',
      width: ACTIONS_COLUMN_WIDTH,
      fixed: 'right',
      align: 'center',
      render: (_, record, index) => (
        <TaskActivityRowActions
          record={record}
          rowNumber={(queuePagination.current - 1) * queuePagination.pageSize + index + 1}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          onResolve={setResolveTaskId}
          onCompleteRecurring={setCompleteRecurringTaskId}
          onSnooze={setSnoozeTaskId}
        />
      ),
    });
    return cols;
  }, [
    showAssigneeColumn,
    queuePagination.current,
    queuePagination.pageSize,
    userLookup,
    handleView,
    handleEdit,
    handleDelete,
    handleStatusChange,
  ]);

  const stageBreakdownColumns: ColumnsType<StageBreakdownRow> = useMemo(
    () => [
      { title: 'Stage', dataIndex: 'label', key: 'label', ellipsis: true },
      {
        title: 'Deals',
        dataIndex: 'openDealCount',
        key: 'openDealCount',
        width: 72,
        align: 'right',
      },
      {
        title: 'Targeted',
        dataIndex: 'targetedAmountSum',
        key: 'targetedAmountSum',
        width: 104,
        align: 'right',
        render: (v: number) => formatAmount(v),
      },
      {
        title: 'Conv.',
        dataIndex: 'conversionPercentFromPrevious',
        key: 'conversionPercentFromPrevious',
        width: 72,
        align: 'right',
        render: (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`),
      },
    ],
    []
  );

  const loadingSkeleton = (
    <>
      <BusinessOverviewSection title="Tasks" subtitle="Loading task metrics…">
        <Col span={24}>
          <Card bordered={false} style={{ borderRadius: token.borderRadiusLG }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        </Col>
      </BusinessOverviewSection>
      <BusinessOverviewSection title="Execution queue" subtitle="Loading prioritized work…">
        <Col span={24}>
          <Card bordered={false} style={{ borderRadius: token.borderRadiusLG }}>
            <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 10 }} />
          </Card>
        </Col>
      </BusinessOverviewSection>
      <BusinessOverviewSection title="Pipeline funnel" subtitle="Loading stage snapshot…">
        <Col xs={24} xl={14}>
          <Card bordered={false} style={{ borderRadius: token.borderRadiusLG, minHeight: 320 }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card bordered={false} style={{ borderRadius: token.borderRadiusLG, minHeight: 320 }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </Col>
      </BusinessOverviewSection>
      <Col span={24}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Col>
    </>
  );

  if (isLoading && data === undefined) {
    return (
      <div className="task-dashboard-page">
        <DashboardShell filters={filters} onFiltersChange={setFilters}>
          {loadingSkeleton}
        </DashboardShell>
      </div>
    );
  }

  if (isError) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load Task Dashboard.';
    return (
      <div className="task-dashboard-page">
        <DashboardShell filters={filters} onFiltersChange={setFilters}>
          <Col span={24}>
            <Alert
              type="error"
              message="Could not load Task Dashboard"
              description={errorMessage}
              showIcon
              action={
                <Button onClick={() => refetch()} type="primary" size="small" icon={<RefreshCw size={14} />}>
                  Retry
                </Button>
              }
            />
          </Col>
        </DashboardShell>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="task-dashboard-page">
        <DashboardShell filters={filters} onFiltersChange={setFilters}>
          <Col span={24}>
            <Empty description="No data returned." />
          </Col>
        </DashboardShell>
      </div>
    );
  }

  const sectionCardStyle = {
    height: '100%',
    background: token.colorBgContainer,
    borderRadius: token.borderRadiusLG,
  };

  const staleNote =
    isPlaceholderData ? (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Showing previous results while updating…
      </Text>
    ) : undefined;

  const executionRows = buildExecutionQueueRows(data);
  const chartStages = taskStagesToChartItems(data.stage);
  const stageBreakdownRows = buildStageBreakdown(data.stage);

  const overdueShown = data.operational.overdue.items.length;
  const dueTodayShown = data.operational.dueToday.items.length;
  const weekShown = data.operational.upcomingWeek.items.length;
  const otherOpenShown = data.operational.otherOpen.items.length;

  const executionFooter =
    data.operational.overdue.count +
      data.operational.dueToday.count +
      data.operational.upcomingWeek.count +
      data.operational.otherOpen.count ===
    0
      ? 'No tasks match filters for these queues.'
      : `Totals — overdue: ${data.operational.overdue.count}, due today: ${data.operational.dueToday.count}, next 7 days: ${data.operational.upcomingWeek.count}, other open: ${data.operational.otherOpen.count}. Up to ${LIST_CAP} tasks fetched per bucket (${overdueShown} · ${dueTodayShown} · ${weekShown} · ${otherOpenShown} rows); table rows are unique by task.`;

  const queueCardExtra = (
    <Space size={4}>
      <CheckSquare size={14} style={{ color: token.colorTextSecondary }} />
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        dropdownRender={() => (
          <div
            style={{
              background: token.colorBgElevated,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowSecondary,
              padding: 12,
              minWidth: 232,
            }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Queue options
              </Text>
              <Button
                type="link"
                block
                style={{ justifyContent: 'flex-start', paddingInline: 0 }}
                icon={<RefreshCw size={14} />}
                onClick={() => {
                  void refetch();
                }}
              >
                Refresh queue
              </Button>
              <Button
                type="link"
                block
                style={{ justifyContent: 'flex-start', paddingInline: 0 }}
                icon={<List size={14} />}
                onClick={() => navigate(appPath('/tasks'))}
              >
                {`Open ${ACTIVITIES_NAV_ITEM}`}
              </Button>
              <div
                style={{
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                  paddingTop: 8,
                }}
              >
              <Checkbox
                checked={showAssigneeColumn}
                onChange={(e) => setShowAssigneeColumn(e.target.checked)}
              >
                Show assignee column
              </Checkbox>
            </div>
            </Space>
          </div>
        )}
      >
        <Button type="text" icon={<Settings size={14} />} aria-label="Queue options" />
      </Dropdown>
    </Space>
  );

  const mainContent = (
    <>
      <BusinessOverviewSection
        title="Tasks"
        subtitle="With a reporting period selected, counts include tasks created, completed, cancelled (updated), in progress or blocked (updated), or due in that window (subject to filters below). Clearing the reporting range uses all-time task history."
      >
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', {}, TASK_FILTER_FIELDS)}
          >
            <Statistic
              title="Total tasks"
              value={data.taskMetrics.totalInReportingPeriod}
              prefix={<List size={16} />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Matching period rule above
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', { statusFilter: 0 }, TASK_FILTER_FIELDS)}
          >
            <Statistic title="Open" value={data.taskMetrics.openStatusCount} prefix={<Inbox size={16} />} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Status
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', { statusFilter: 1 }, TASK_FILTER_FIELDS)}
          >
            <Statistic
              title="In progress"
              value={data.taskMetrics.inProgressCount}
              prefix={<RefreshCw size={16} />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Status
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', { statusFilter: 2 }, TASK_FILTER_FIELDS)}
          >
            <Statistic title="Blocked" value={data.taskMetrics.blockedCount} prefix={<CircleStop size={16} />} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Status
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', { statusFilter: 3 }, TASK_FILTER_FIELDS)}
          >
            <Statistic
              title="Completed in period"
              value={data.taskMetrics.completedInPeriodCount}
              prefix={<CheckSquare size={16} />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              CompletedAt in window
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', { statusFilter: 4 }, TASK_FILTER_FIELDS)}
          >
            <Statistic
              title="Cancelled"
              value={data.taskMetrics.cancelledInPeriodCount}
              prefix={<MinusCircle size={16} />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Status
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() =>
              navTo(
                '/tasks',
                { statusFilter: 0, taskTypeFilter: TASK_TYPE_INTERNAL },
                TASK_FILTER_FIELDS,
              )
            }
          >
            <Statistic title="Internal open" value={data.taskMetrics.internalOpenCount} prefix={<TrendingUp size={16} />} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Completed in period: {data.taskMetrics.completedInternalInPeriod}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/tasks', { isOverdue: true }, TASK_FILTER_FIELDS)}
          >
            <Statistic
              title="Overdue tasks"
              value={data.taskMetrics.overdueCount}
              prefix={<AlertCircle size={16} />}
              valueStyle={{ color: token.colorError }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Past due date in backlog
            </Text>
          </Card>
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection
        title="Execution queue"
        subtitle="Open tasks with due dates (UTC). Filtered by segments, categories, and assignees."
      >
        <Col span={24}>
          <Card
            bordered={false}
            title={
              <Space align="center">
                <Zap size={16} style={{ color: token.colorPrimary }} />
                <span>What needs attention</span>
              </Space>
            }
            extra={queueCardExtra}
            style={sectionCardStyle}
            styles={{ body: { ...cardBodyPadding, paddingTop: 12 } }}
          >
            <div className="task-dashboard-queue-table">
              <Table<ExecutionQueueRow>
                rowKey={(row) => row.id}
                columns={executionColumns}
                dataSource={executionRows}
                pagination={{
                  current: queuePagination.current,
                  pageSize: queuePagination.pageSize,
                  total: executionRows.length,
                  showSizeChanger: true,
                  pageSizeOptions: [25, 50, 100],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
                }}
                onChange={(pagination) => {
                  setQueuePagination({
                    current: pagination.current ?? 1,
                    pageSize: pagination.pageSize ?? 25,
                  });
                }}
                size="middle"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Nothing in these queues"
                      styles={{ image: { height: 48 } }}
                    />
                  ),
                }}
                scroll={{ x: 'max-content' }}
              />
            </div>
            <Text type="secondary" className="task-dashboard-queue-footer">
              {executionFooter}
            </Text>
          </Card>
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection
        title="Pipeline funnel"
        subtitle="Open deals at period snapshot, grouped by pipeline stage."
      >
        <Col xs={24} xl={14}>
          <Card
            bordered={false}
            title="Stage distribution"
            style={{ ...sectionCardStyle, minHeight: 360 }}
            styles={{ body: { ...cardBodyPadding, minHeight: 300 } }}
          >
            {chartStages.length === 0 ? (
              <Empty description="No open deals match the current filters." />
            ) : (
              <div className="task-dashboard-funnel-chart-wrap">
                <PipelineStageBarChart stages={chartStages} height={280} />
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card
            bordered={false}
            title="Stage breakdown"
            style={{ ...sectionCardStyle, minHeight: 360 }}
            styles={{ body: cardBodyPadding }}
          >
            <Table<StageBreakdownRow>
              rowKey="stageId"
              columns={stageBreakdownColumns}
              dataSource={stageBreakdownRows}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No open pipeline in this view' }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Col>
      </BusinessOverviewSection>
    </>
  );

  return (
    <div
      className="task-dashboard-page"
      style={{ position: 'relative', opacity: isFetching && !isLoading ? 0.92 : 1, transition: 'opacity 0.2s' }}
    >
      <DashboardShell filters={filters} onFiltersChange={setFilters} staleNote={staleNote}
        actions={
          <Dropdown
            menu={{ items: dashboardExportMenuItems, onClick: ({ key }) => handleExportDashboard(key as 'excel' | 'pdf') }}
            trigger={['click']}
          >
            <Button
              icon={<Download size={14} />}
              loading={exportMutation.isPending || pdfExportMutation.isPending}
            >
              Export
            </Button>
          </Dropdown>
        }
      >
        {mainContent}
      </DashboardShell>

      <TaskFormDrawer
        open={formDrawerOpen}
        onClose={() => {
          setFormDrawerOpen(false);
          setEditTaskId(null);
        }}
        onSuccess={handleFormSuccess}
        editTaskId={editTaskId}
        defaultTaskType={TASK_TYPE_INTERNAL}
      />

      <TaskDetailDrawer
        open={viewTaskId !== null}
        onClose={() => setViewTaskId(null)}
        taskId={viewTaskId}
      />

      <ResolveFollowupModal
        open={resolveTaskId !== null}
        taskId={resolveTaskId}
        loading={resolveMutation.isPending}
        onCancel={() => setResolveTaskId(null)}
        onSubmit={(taskId, payload) => resolveMutation.mutate({ taskId, payload })}
      />

      <SnoozeFollowupModal
        open={snoozeTaskId !== null}
        taskId={snoozeTaskId}
        loading={snoozeMutation.isPending}
        onCancel={() => setSnoozeTaskId(null)}
        onSubmit={(taskId, newDueDate, reason) =>
          snoozeMutation.mutate({ taskId, newDueDate, reason })
        }
      />

      <CompleteRecurringModal
        open={completeRecurringTaskId !== null}
        taskId={completeRecurringTaskId}
        loading={completeRecurringMutation.isPending}
        onCancel={() => setCompleteRecurringTaskId(null)}
        onSubmit={(taskId, payload) => completeRecurringMutation.mutate({ taskId, payload })}
      />
    </div>
  );
};

export default TaskDashboardPage;
